import { FC, memo } from "react";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import type { AlarmException } from "@src/services/api/types";
import { useUnistyles } from "react-native-unistyles";
import Svg, { Rect, Defs, Pattern as SvgPattern, Path } from "react-native-svg";
import { isRuleArmed, type SlotType, type DaySegment } from "@src/features/schedules/utils/schedule-utils";
import { useAlarmStateBackgroundColor, useStripeColor } from "@src/features/schedules/utils/stripe-pattern";

interface ScheduleBarSvgProps {
  segments: DaySegment[];
  isPastDay: boolean;
  barHeight: number;
  exceptions?: AlarmException[];
  exceptionColors: Record<string, string>;
  schedule?: AlarmSchedule;
}

export const ScheduleBarSvg: FC<ScheduleBarSvgProps> = memo(
  ({ segments, isPastDay, barHeight, exceptions, exceptionColors, schedule }) => {
    const { theme } = useUnistyles();
    const getAlarmStateBackgroundColor = useAlarmStateBackgroundColor();
    const getStripeColor = useStripeColor();

    const isException = (segType: SlotType): boolean => {
      return segType !== "armed" && segType !== "disarmed";
    };

    const getExceptionAction = (segType: SlotType): "armed" | "disarmed" | null => {
      if (!isException(segType)) return null;
      const exception = exceptions?.find((ex) => ex.id === segType);
      return exception?.action ?? null;
    };

    const getOverrideAction = (segType: SlotType): "armed" | "disarmed" | null => {
      if (!segType.startsWith("override:")) return null;
      const rule = schedule?.rules.find((r) => r.id === segType);
      if (!rule) return null;
      return isRuleArmed(rule.features) ? "armed" : "disarmed";
    };

    const getStripeAction = (segType: SlotType): "armed" | "disarmed" | null => {
      return getExceptionAction(segType) ?? getOverrideAction(segType);
    };

    const getSegmentColor = (segType: SlotType): string => {
      if (segType === "armed")
        return (isPastDay ? theme.colors.alarmStates.armedSecondary : theme.colors.alarmStates.armed) as string;
      if (segType === "disarmed") return "transparent";
      if (segType.startsWith("silence:")) return theme.colors.text as string;

      const stripeAction = getStripeAction(segType);
      if (stripeAction) return getAlarmStateBackgroundColor(stripeAction);
      if (exceptionColors[segType]) return exceptionColors[segType];
      return "transparent";
    };

    return (
      <Svg width="100%" height={barHeight}>
        {/* Pattern defs for diagonal exception stripes */}
        {/* @ts-expect-error baseline 2026-02-26 - react-native-svg Defs children typing incomplete with React 19 types */}
        <Defs>
          <SvgPattern id="stripe-armed" patternUnits="userSpaceOnUse" width="9" height="5">
            <Rect width="9" height="5" fill={getAlarmStateBackgroundColor("armed")} />
            <Path
              d="M-9,5 l9,-5 M0,5 l9,-5 M9,5 l9,-5"
              stroke={getStripeColor("armed")}
              strokeWidth="1"
              strokeLinecap="square"
            />
          </SvgPattern>
          <SvgPattern id="stripe-disarmed" patternUnits="userSpaceOnUse" width="9" height="5">
            <Rect width="9" height="5" fill={getAlarmStateBackgroundColor("disarmed")} />
            <Path
              d="M-9,5 l9,-5 M0,5 l9,-5 M9,5 l9,-5"
              stroke={getStripeColor("disarmed")}
              strokeWidth="1"
              strokeLinecap="square"
            />
          </SvgPattern>
        </Defs>

        {/* Segment rects */}
        {segments.map((seg, i) => {
          if (seg.type === "disarmed") return null;

          const y = (seg.startPercent / 100) * barHeight;
          const h = ((seg.endPercent - seg.startPercent) / 100) * barHeight;
          const isStripeSeg = isException(seg.type) && !seg.type.startsWith("silence:");

          if (isStripeSeg) {
            const action = getStripeAction(seg.type);
            return (
              <Rect
                key={i}
                transform={`translate(0, ${y})`}
                width="100%"
                height={h}
                fill={action ? `url(#stripe-${action})` : getSegmentColor(seg.type)}
              />
            );
          }

          return (
            <Rect key={i} transform={`translate(0, ${y})`} width="100%" height={h} fill={getSegmentColor(seg.type)} />
          );
        })}
      </Svg>
    );
  },
);
